import { useState, useEffect, useRef, useCallback } from "react";
import { Radio, Mic, MicOff, Video, Loader2, Sparkles, Square, Play, MessageCircle, Eye, HelpCircle, Save, CheckCircle2, Info, ExternalLink } from "lucide-react";
import AppLayout from "@/components/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CHUNK_MS = 15000;      // 15s Whisper chunks
const SUGGEST_EVERY_N_CHUNKS = 2;  // Fire the LLM suggestion every ~30s

const CATEGORY_STYLE = {
  cheque_size: "bg-emerald-50 text-emerald-800 border-emerald-200",
  thesis:      "bg-teal-50 text-teal-800 border-teal-200",
  timing:      "bg-amber-50 text-amber-800 border-amber-200",
  concern:     "bg-red-50 text-red-800 border-red-200",
  reference:   "bg-slate-50 text-slate-700 border-slate-200",
  conflict:    "bg-orange-50 text-orange-800 border-orange-200",
  next_step:   "bg-cyan-50 text-cyan-800 border-cyan-200",
  commitment:  "bg-purple-50 text-purple-800 border-purple-200",
  other:       "bg-slate-50 text-slate-600 border-slate-200",
};

export default function FundraisingCall() {
  const [user, setUser] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [investors, setInvestors] = useState([]);
  const [selectedInvestorId, setSelectedInvestorId] = useState("");
  const [meetLink, setMeetLink] = useState("");
  const [session, setSession] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [intel, setIntel] = useState([]);
  const [summary, setSummary] = useState("");
  const [recording, setRecording] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [mediaError, setMediaError] = useState("");
  const [chunksUploaded, setChunksUploaded] = useState(0);

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunkCounterRef = useRef(0);
  const transcriptEndRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const me = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" });
        if (me.ok) setUser(await me.json());
        const inv = await fetch(`${BACKEND_URL}/api/pipeline`, { credentials: "include" });
        if (inv.ok) {
          const d = await inv.json();
          const pip = d.pipeline || {};
          const flat = [];
          Object.values(pip).forEach((arr) => (arr || []).forEach((item) => {
            flat.push({
              investor_id: item.investor_id || item.pipeline_id,
              pipeline_id: item.pipeline_id,
              name: item.firm_name || item.investor_name || item.pipeline_id,
              stage: item.stage,
            });
          }));
          setInvestors(flat);
        }
      } catch (e) { /* silent */ }
      setPageLoading(false);
    })();
  }, []);

  useEffect(() => { transcriptEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [transcript.length]);

  // ------------------------ Recording + chunk upload ------------------------
  const startCall = async () => {
    // 1. Create session record on backend
    const chosen = investors.find((i) => i.investor_id === selectedInvestorId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/fundraising/call/start`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({
          investor_id: selectedInvestorId,
          pipeline_id: chosen?.pipeline_id || "",
          investor_name: chosen?.name || "",
          meet_link: meetLink,
        }),
      });
      if (!res.ok) { toast.error("Could not start call session"); return; }
      const s = await res.json();
      setSession(s);
      setTranscript([]); setSuggestions([]); setIntel([]); setSummary("");
      chunkCounterRef.current = 0;
      setChunksUploaded(0);
      toast.success(`Call started${s.investor_name ? ` — ${s.investor_name}` : ""}`);
    } catch (e) { toast.error("Failed to start"); return; }

    // 2. Kick off mic capture
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setMediaError("");
      startChunkedRecording(stream);
      setRecording(true);
    } catch (e) {
      setMediaError("Microphone access denied. Grant mic permission and click Start again.");
      toast.error("Mic permission blocked");
    }
  };

  const startChunkedRecording = (stream) => {
    // MediaRecorder — cut a fresh recorder every CHUNK_MS so each chunk is a
    // standalone playable webm blob (whisper needs a valid container per file).
    const spinRecorder = () => {
      if (!streamRef.current) return;
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      const chunks = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
      mr.onstop = async () => {
        const blob = new Blob(chunks, { type: "audio/webm" });
        if (blob.size > 1000) await uploadChunk(blob);
        if (streamRef.current) spinRecorder();  // start next chunk
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setTimeout(() => { if (mr.state === "recording") mr.stop(); }, CHUNK_MS);
    };
    spinRecorder();
  };

  const uploadChunk = async (blob) => {
    if (!session) return;
    const form = new FormData();
    form.append("audio", blob, `chunk_${Date.now()}.webm`);
    try {
      const res = await fetch(`${BACKEND_URL}/api/fundraising/call/${session.session_id}/transcribe`, {
        method: "POST", credentials: "include", body: form,
      });
      if (res.ok) {
        const d = await res.json();
        if (d.text) {
          setTranscript((prev) => [...prev, { id: d.segment_id || `tmp_${Date.now()}`, text: d.text, at: d.at || new Date().toISOString() }]);
          chunkCounterRef.current += 1;
          setChunksUploaded((c) => c + 1);
          if (chunkCounterRef.current % SUGGEST_EVERY_N_CHUNKS === 0) fetchSuggestion();
        }
      }
    } catch (e) { /* skip failed chunk */ }
  };

  const fetchSuggestion = async () => {
    if (!session) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/fundraising/call/${session.session_id}/suggest`, {
        method: "POST", credentials: "include",
      });
      if (res.ok) {
        const d = await res.json();
        if (d.suggestion) setSuggestions((prev) => [d.suggestion, ...prev].slice(0, 6));
      }
    } catch (e) { /* silent */ }
  };

  const endCall = async () => {
    if (!session) return;
    setFinalizing(true);
    // Stop the mic
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") mediaRecorderRef.current.stop();
    } catch (e) { /* silent */ }
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    setRecording(false);
    // Finalize on backend (waits a beat for last chunk to upload)
    await new Promise((r) => setTimeout(r, 1200));
    try {
      const res = await fetch(`${BACKEND_URL}/api/fundraising/call/${session.session_id}/finalize`, {
        method: "POST", credentials: "include",
      });
      if (res.ok) {
        const d = await res.json();
        setIntel(d.intel || []); setSummary(d.summary || "");
        setSession((s) => ({ ...s, status: "ended" }));
        toast.success(`Call ended — ${(d.intel || []).length} Intel items captured`);
      } else toast.error("Finalize failed");
    } catch (e) { toast.error("Finalize failed"); }
    setFinalizing(false);
  };

  const resetSession = () => {
    setSession(null); setTranscript([]); setSuggestions([]); setIntel([]); setSummary("");
    setMeetLink(""); setSelectedInvestorId("");
    chunkCounterRef.current = 0; setChunksUploaded(0);
  };

  const openMeet = () => {
    const url = meetLink.trim() || "https://meet.new";
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (pageLoading) return <AppLayout user={user}><div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-teal-500" /></div></AppLayout>;

  const trackedByInvestorId = new Map(investors.map((i) => [i.investor_id, i]));
  const latestSuggestion = suggestions[0];

  return (
    <AppLayout user={user}>
      <div className="max-w-[1400px]" data-testid="fundraising-call-page">
        <div className="mb-6 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center"><Radio className="w-5 h-5 text-teal-600" /></div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Investor Call</h1>
            <p className="text-sm text-slate-500">Live call companion — mic → Whisper every 15s → live suggestions and auto-Intel to CRM.</p>
          </div>
        </div>

        {/* Setup / Start bar */}
        {!session && (
          <Card data-testid="call-setup">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-800">Set up the call</CardTitle>
              <p className="text-xs text-slate-500">Link a tracked investor (optional) and paste your Meet link. Then open Meet in a new tab and click Start.</p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wide">Investor (optional)</label>
                  <select
                    value={selectedInvestorId}
                    onChange={(e) => setSelectedInvestorId(e.target.value)}
                    className="w-full mt-1 border rounded h-9 px-2 text-sm bg-white"
                    data-testid="call-investor-select"
                  >
                    <option value="">— Unlinked call —</option>
                    {investors.map((i) => (
                      <option key={i.investor_id} value={i.investor_id}>{i.name || i.firm_name || i.investor_id}</option>
                    ))}
                  </select>
                  {selectedInvestorId && trackedByInvestorId.get(selectedInvestorId) && (
                    <p className="text-[10px] text-slate-400 mt-1">Intel from this call will be appended to <span className="font-medium">{trackedByInvestorId.get(selectedInvestorId).name || "this investor"}</span>&apos;s CRM card.</p>
                  )}
                </div>
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wide">Google Meet link (optional)</label>
                  <div className="flex gap-2 mt-1">
                    <Input value={meetLink} onChange={(e) => setMeetLink(e.target.value)} placeholder="https://meet.google.com/xxx-yyyy-zzz" className="h-9 text-sm" data-testid="call-meet-input" />
                    <Button variant="outline" onClick={openMeet} className="h-9 px-3 shrink-0" data-testid="call-meet-open"><Video className="w-4 h-4 mr-1.5" /> Open Meet</Button>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1">No link? Click Open Meet to jump to <span className="font-mono">meet.new</span> and start a fresh call.</p>
                </div>
              </div>
              <div className="bg-teal-50 border border-teal-200 rounded p-3 text-xs text-teal-900" data-testid="call-privacy">
                <p className="flex items-start gap-1.5"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span>Operon captures YOUR microphone only (which picks up the investor through your speakers). Nothing is streamed to the investor. Audio chunks are sent to OpenAI Whisper for transcription and discarded after processing.</span></p>
              </div>
              <div className="flex justify-end">
                <Button onClick={startCall} className="bg-red-600 hover:bg-red-700 text-white" data-testid="call-start-btn">
                  <Mic className="w-4 h-4 mr-1.5" /> Start call &amp; recording
                </Button>
              </div>
              {mediaError && <p className="text-xs text-red-600" data-testid="call-mic-error">{mediaError}</p>}
            </CardContent>
          </Card>
        )}

        {/* Live call view */}
        {session && (
          <div className="space-y-4">
            {/* Live status bar */}
            <Card>
              <CardContent className="pt-4 pb-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  {recording ? (
                    <span className="flex items-center gap-1.5 text-sm font-medium text-red-600"><span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />Live</span>
                  ) : session.status === "ended" ? (
                    <span className="flex items-center gap-1.5 text-sm font-medium text-emerald-700"><CheckCircle2 className="w-4 h-4" />Ended</span>
                  ) : (
                    <span className="flex items-center gap-1.5 text-sm font-medium text-slate-500"><MicOff className="w-4 h-4" />Paused</span>
                  )}
                  {session.investor_name && <Badge variant="outline">{session.investor_name}</Badge>}
                  <span className="text-xs text-slate-500">{chunksUploaded} chunk{chunksUploaded === 1 ? "" : "s"} · {transcript.length} segments</span>
                </div>
                <div className="flex items-center gap-2">
                  {session.meet_link && (
                    <Button variant="outline" size="sm" onClick={openMeet} data-testid="call-open-meet-again">
                      <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Meet
                    </Button>
                  )}
                  {recording && (
                    <Button onClick={endCall} disabled={finalizing} className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="call-end-btn">
                      {finalizing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Square className="w-4 h-4 mr-1.5" />} End &amp; save
                    </Button>
                  )}
                  {!recording && session.status === "ended" && (
                    <Button onClick={resetSession} variant="outline" data-testid="call-new-btn">
                      <Play className="w-4 h-4 mr-1.5" /> New call
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Live layout — Transcript (left) + Suggestions (right) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Card className="lg:col-span-2" data-testid="call-transcript">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-slate-800 flex items-center gap-2"><Mic className="w-4 h-4 text-red-500" /> Live transcript</CardTitle>
                  <p className="text-xs text-slate-500">Rolling text from your microphone. Shows both sides of the call (investor via your speakers).</p>
                </CardHeader>
                <CardContent>
                  <div className="max-h-[520px] overflow-y-auto border rounded-lg bg-slate-50/40 p-3 space-y-1.5" data-testid="call-transcript-scroll">
                    {transcript.length === 0 && (
                      <p className="text-xs text-slate-400 italic text-center py-8">Waiting for the first 15s chunk to transcribe…</p>
                    )}
                    {transcript.map((s) => (
                      <div key={s.id} className="text-sm text-slate-800 leading-relaxed border-l-2 border-l-slate-200 pl-2 py-0.5" data-testid={`transcript-${s.id}`}>
                        {s.text}
                      </div>
                    ))}
                    <div ref={transcriptEndRef} />
                  </div>
                </CardContent>
              </Card>

              <Card data-testid="call-suggestions">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm text-slate-800 flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-500" /> Live suggestions</CardTitle>
                    {recording && <Button size="sm" variant="ghost" onClick={fetchSuggestion} className="h-6 px-2 text-amber-600" data-testid="call-suggest-now"><Sparkles className="w-3.5 h-3.5" /></Button>}
                  </div>
                  <p className="text-xs text-slate-500">Refreshes every ~30s. Click sparkle to force a fresh pass.</p>
                </CardHeader>
                <CardContent>
                  {!latestSuggestion && (
                    <p className="text-xs text-slate-400 italic text-center py-6">Suggestions will appear here after ~30s of transcript.</p>
                  )}
                  {latestSuggestion && (
                    <div className="space-y-3">
                      <div className="border-l-4 border-l-amber-500 bg-amber-50 rounded p-2.5">
                        <p className="text-[10px] font-medium uppercase tracking-wide text-amber-700 flex items-center gap-1"><MessageCircle className="w-3 h-3" /> Angle right now</p>
                        <p className="text-sm text-slate-800 leading-snug mt-1" data-testid="call-sugg-angle">{latestSuggestion.angle}</p>
                      </div>
                      {latestSuggestion.questions?.length > 0 && (
                        <div>
                          <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500 flex items-center gap-1"><HelpCircle className="w-3 h-3" /> Ask next</p>
                          <ul className="mt-1 space-y-1 text-xs text-slate-800" data-testid="call-sugg-questions">
                            {latestSuggestion.questions.map((q, i) => <li key={i} className="flex items-start gap-1"><span className="text-slate-400">•</span><span>{q}</span></li>)}
                          </ul>
                        </div>
                      )}
                      {latestSuggestion.watch_for?.length > 0 && (
                        <div>
                          <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500 flex items-center gap-1"><Eye className="w-3 h-3" /> Watch for</p>
                          <ul className="mt-1 space-y-1 text-xs text-slate-800" data-testid="call-sugg-watch">
                            {latestSuggestion.watch_for.map((w, i) => <li key={i} className="flex items-start gap-1"><span className="text-slate-400">•</span><span>{w}</span></li>)}
                          </ul>
                        </div>
                      )}
                      {suggestions.length > 1 && (
                        <details className="text-xs text-slate-500">
                          <summary className="cursor-pointer">Older passes ({suggestions.length - 1})</summary>
                          <div className="mt-2 space-y-2">
                            {suggestions.slice(1).map((s) => (
                              <div key={s.id} className="border rounded p-2 bg-slate-50">
                                <p className="text-[10px] text-slate-400">{new Date(s.at).toLocaleTimeString()}</p>
                                <p className="text-slate-700 mt-1">{s.angle}</p>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Post-call: summary + intel */}
            {session.status === "ended" && (
              <Card data-testid="call-postcall">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-slate-800 flex items-center gap-2"><Save className="w-4 h-4 text-emerald-600" /> Call captured</CardTitle>
                  {session.investor_name && <p className="text-xs text-slate-500">Intel pushed to <span className="font-medium text-teal-700">{session.investor_name}</span>&apos;s CRM card.</p>}
                </CardHeader>
                <CardContent className="space-y-3">
                  {summary && (
                    <div className="bg-teal-50 border border-teal-200 rounded p-3 text-sm text-teal-900" data-testid="call-summary">
                      <p className="text-[10px] uppercase tracking-wide text-teal-700 font-medium mb-1">Summary</p>
                      <p className="leading-relaxed">{summary}</p>
                    </div>
                  )}
                  {intel.length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-slate-500 font-medium mb-2">Intel captured ({intel.length})</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {intel.map((i) => (
                          <div key={i.id} className={`border rounded-lg p-2.5 ${CATEGORY_STYLE[i.category] || CATEGORY_STYLE.other}`} data-testid={`intel-${i.id}`}>
                            <Badge variant="outline" className="text-[10px] mb-1 uppercase">{i.category.replace(/_/g, " ")}</Badge>
                            <p className="text-xs leading-snug">{i.note}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
